/**
 * Whether the Points Capture extension running in this browser is behind, and
 * what it takes to catch up. One definition, read by Wallet's Refresh page and
 * by the extension's popup, so the two cannot disagree about the same build.
 *
 * Three facts go in. `latest` is the version Wallet's web build shipped with,
 * from `extension/package.json`, which only a shipping commit bumps. `running`
 * is the build Chrome has loaded. `onDisk` is the build sitting in the unpacked
 * extension's directory, read fresh from `build-info.json`: a different build
 * there means a reload alone picks it up, and none means it must be built first.
 *
 * Carries no chrome and no DOM types, like `capturePort.ts` beside it.
 */

export interface ExtensionBuild {
  version: string;
  commit: string;
  built: string;
}

/** The file `extension/build.mjs` writes into `dist/`, and the path it is read at. */
export const BUILD_INFO_FILE = "build-info.json";

/** Where Wallet's web build publishes `latest`, for the popup to read. */
export const LATEST_VERSION_FILE = "extension-version.json";

export type UpdateState =
  /** Nothing to do, or nothing can be told. */
  | { kind: "current" }
  /** A different build is on disk: reloading picks it up. */
  | { kind: "ready"; onDisk: ExtensionBuild }
  /** A newer version shipped and is not built here yet. */
  | { kind: "build"; latest: string };

/** -1, 0 or 1 for dotted numeric versions; a non-numeric part compares as 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((n) => Number.parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => Number.parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export function isBuild(value: unknown): value is ExtensionBuild {
  if (typeof value !== "object" || value === null) return false;
  const b = value as Record<string, unknown>;
  return typeof b.version === "string" && typeof b.commit === "string"
    && typeof b.built === "string";
}

/**
 * The update state for one running build.
 *
 * A build on disk that differs from the running one wins over a newer shipped
 * version: whatever is on disk is what a reload loads, so it is the next step
 * either way. A development rebuild counts too, which answers "did I reload
 * after building". `onDisk` null means it could not be read (an older build
 * writes no file), and then only the shipped version can be compared.
 */
export function updateState(
  running: ExtensionBuild,
  onDisk: ExtensionBuild | null,
  latest: string | null,
): UpdateState {
  if (onDisk && onDisk.built !== running.built) return { kind: "ready", onDisk };
  if (latest && compareVersions(latest, running.version) > 0) return { kind: "build", latest };
  return { kind: "current" };
}

/** The command that brings the main checkout's build up to date. */
export const BUILD_COMMAND =
  "git -C ~/workspace/wallet-portal pull && npm --prefix ~/workspace/wallet-portal/extension run build";
